import { Injectable } from '@angular/core';
import { FieldConfiguration } from '../models/field-configuration';
import { AccountSettingsService } from '@core/services/account-settings.service';
import { PageName } from '@shared/enums/page-name';

/**
 * Key of a fields configuration: a page, or a derived key for pages that own
 * several field sets (e.g. `${PageName.BillOfMaterial}--otherCosts`).
 */
export type FieldsConfigurationKey = PageName | string;

@Injectable({
  providedIn: 'root'
})
export class FieldsConfigurationService {

  /**
   * Default configurations registered at runtime (e.g. derived from a
   * `TransactionDefinition`). Takes precedence over the static switch below,
   * so new pages don't need to edit this shared service.
   */
  private readonly registeredDefaults = new Map<FieldsConfigurationKey, FieldConfiguration[]>();

  constructor(private accountSettingsService: AccountSettingsService) { }

  registerDefaultConfiguration(pageName: FieldsConfigurationKey, configuration: FieldConfiguration[]): void {
    this.registeredDefaults.set(pageName, configuration.map(f => ({ ...f })));
  }

  async getFieldsConfiguration(pageName: FieldsConfigurationKey): Promise<FieldConfiguration[]> {
    const defaultConfig = this.getDefaultConfiguration(pageName);

    try {
      const stored = await this.accountSettingsService.loadFieldsConfiguration(pageName);
      if (Array.isArray(stored)) {
        return this.mergeWithDefaults(stored, defaultConfig);
      }
    } catch (error) {
      console.error('Error loading field configuration from Firestore:', error);
    }

    return defaultConfig;
  }

  private mergeWithDefaults(stored: FieldConfiguration[], defaultConfig: FieldConfiguration[]): FieldConfiguration[] {
    const defaultByField = new Map(defaultConfig.map(f => [f.field, f]));
    const storedFields = new Set(stored.map(f => f.field));
    const merged = stored.map(f => {
      const def = defaultByField.get(f.field);
      // Pull non-user-controlled flags from defaults so server-side changes propagate
      // to users who already have a saved configuration.
      return def
        ? { ...f, canChangeVisibility: def.canChangeVisibility, isPositionLocked: def.isPositionLocked }
        : f;
    });
    let maxOrder = Math.max(0, ...stored.map(f => f.order));

    for (const defField of defaultConfig) {
      if (!storedFields.has(defField.field)) {
        merged.push({ ...defField, order: ++maxOrder });
      }
    }

    return merged.sort((a, b) => a.order - b.order);
  }

  async saveFieldsConfiguration(pageName: FieldsConfigurationKey, configuration: FieldConfiguration[]): Promise<void> {
    try {
      await this.accountSettingsService.saveFieldsConfiguration(pageName, configuration);
    } catch (error) {
      console.error('Error saving field configuration to Firestore:', error);
      throw error;
    }
  }

  getDefaultConfiguration(pageName: FieldsConfigurationKey): FieldConfiguration[] {
    const registered = this.registeredDefaults.get(pageName);
    if (registered) {
      return registered.map(f => ({ ...f }));
    }

    switch (pageName) {
      case PageName.PurchasingOrders:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'quantity', order: 2, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'discount', order: 4, isVisible: true, canChangeVisibility: true },
          { field: 'tax', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'lineTotal', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'comments', order: 7, isVisible: false, canChangeVisibility: true },
          { field: 'sku', order: 8, isVisible: false, canChangeVisibility: true },
          { field: 'partNumber', order: 9, isVisible: false, canChangeVisibility: true },
          { field: 'brand', order: 10, isVisible: false, canChangeVisibility: true }
        ];
      case PageName.PurchaseRequests:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'quantity', order: 2, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'total', order: 4, isVisible: true, canChangeVisibility: true },
          { field: 'comments', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'sku', order: 6, isVisible: false, canChangeVisibility: true },
          { field: 'partNumber', order: 7, isVisible: false, canChangeVisibility: true },
          { field: 'brand', order: 8, isVisible: false, canChangeVisibility: true }
        ];
      case PageName.PurchaseInvoices:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'description', order: 2, isVisible: false, canChangeVisibility: true },
          { field: 'quantity', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: false },
          { field: 'lineDiscount', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'tax', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'lineTotal', order: 7, isVisible: true, canChangeVisibility: true },
          { field: 'sku', order: 8, isVisible: false, canChangeVisibility: true },
          { field: 'partNumber', order: 9, isVisible: false, canChangeVisibility: true },
          { field: 'brand', order: 10, isVisible: false, canChangeVisibility: true }
        ];
      case PageName.SalesOrdersV2_Form:
        return [
          { field: 'image', order: 0, isVisible: false, canChangeVisibility: true, isPositionLocked: true },
          { field: 'item', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'quantity', order: 2, isVisible: true, canChangeVisibility: false },
          { field: 'uom', order: 3, isVisible: true, canChangeVisibility: true },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: true },
          { field: 'grossTotal', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'discount', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'serials', order: 7, isVisible: true, canChangeVisibility: true },
          { field: 'batchNo', order: 8, isVisible: true, canChangeVisibility: true },
          { field: 'balance', order: 9, isVisible: true, canChangeVisibility: true }
        ];
      case PageName.SalesOrdersV2_ExchangeForm:
        return [
          { field: 'image', order: 0, isVisible: false, canChangeVisibility: true, isPositionLocked: true },
          { field: 'item', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'quantity', order: 2, isVisible: true, canChangeVisibility: false },
          { field: 'uom', order: 3, isVisible: true, canChangeVisibility: true },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: true },
          { field: 'grossTotal', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'discount', order: 6, isVisible: true, canChangeVisibility: true }
        ];
      case PageName.SalesInvoices:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'description', order: 2, isVisible: false, canChangeVisibility: true },
          { field: 'quantity', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: false },
          { field: 'lineDiscount', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'tax', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'withHolding', order: 7, isVisible: false, canChangeVisibility: true },
          { field: 'lineTotal', order: 8, isVisible: true, canChangeVisibility: true }
        ];
      case PageName.CreditNotes:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'description', order: 2, isVisible: false, canChangeVisibility: true },
          { field: 'quantity', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: false },
          { field: 'discount', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'tax', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'withHolding', order: 7, isVisible: false, canChangeVisibility: true },
          { field: 'lineTotal', order: 8, isVisible: true, canChangeVisibility: true },
        ];
      case PageName.DebitNotes:
        return [
          { field: 'product', order: 1, isVisible: true, canChangeVisibility: false },
          { field: 'description', order: 2, isVisible: false, canChangeVisibility: true },
          { field: 'quantity', order: 3, isVisible: true, canChangeVisibility: false },
          { field: 'price', order: 4, isVisible: true, canChangeVisibility: false },
          { field: 'lineDiscount', order: 5, isVisible: true, canChangeVisibility: true },
          { field: 'tax', order: 6, isVisible: true, canChangeVisibility: true },
          { field: 'lineTotal', order: 7, isVisible: true, canChangeVisibility: true }
        ];
      default:
        return [];
    }
  }

  async getVisibleFieldsInOrderAsync(pageName: FieldsConfigurationKey): Promise<string[]> {
    const configuration = await this.getFieldsConfiguration(pageName);
    return configuration
      .filter(field => field.isVisible)
      .sort((a, b) => a.order - b.order)
      .map(field => field.field);
  }

}
